import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import Datamap from 'datamaps';
import api from '../api/api';
import Seo from '../components/Seo';
var CDN = process.env.NODE_ENV==='development' ? 'https://cdn.j7lab.com/dev' : 'https://cdn.j7lab.com';
// Fonction utilitaire pour vérifier si un mot commence par une voyelle ou un h muet
const startsWithVowelSound = (word) => {
  if (!word) return false;
  const firstLetter = word.toLowerCase().charAt(0);
  // Voyelles et h muet (on considère h comme muet par défaut en français)
  return /^[aeiouàâäéèêëïîôöùûüÿh]/.test(firstLetter);
};

// Fonction utilitaire pour formater une liste d'éléments en français (virgules + "et")
const formatFrenchList = (items) => {
  if (!items || items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} et ${items[1]}`;
  
  const allButLast = items.slice(0, -1);
  const last = items[items.length - 1];
  return `${allButLast.join(', ')} et ${last}`;
};

// Fonction utilitaire pour créer l'URL de la page combo
const generateComboUrl = (combo) => {
  if (!combo?._id || !combo?.itemsList?.length) return '#';
  
  // prendre le slug du combo
  
  const comboSlug =combo.slug
  return `/idees-recettes/combinaison-avec/${comboSlug}/${combo._id}`;
};

// Fonction utilitaire pour obtenir l'article approprié
const getArticle = (seoData, preposition = false, nextWord = '') => {
  if (!seoData) return preposition ? 'de ' : 'le ';
  
  const isPlural = seoData.number === "pluriel";
  const isFeminine = seoData.gender === "feminin";
  const startsWithVowel = startsWithVowelSound(nextWord);
  
  if (preposition) {
    if (isPlural) return "des ";
    if (startsWithVowel) return "de l'";
    return isFeminine ? "de la " : "du ";
  }
  
  if (isPlural) return "les ";
  if (startsWithVowel) return "l'";
  return isFeminine ? "la " : "le ";
};

// Fonction utilitaire pour obtenir l'article approprié avec première lettre majuscule
const getArticleCapitalized = (seoData, nextWord = '') => {
  if (!seoData) return 'Le ';
  
  const isPlural = seoData.number === "pluriel";
  const isFeminine = seoData.gender === "feminin";
  const startsWithVowel = startsWithVowelSound(nextWord);
  
  if (isPlural) return "Les ";
  if (startsWithVowel) return "L'";
  return isFeminine ? "La " : "Le ";
};

// Fonction utilitaire pour obtenir l'article possessif
const getPossessiveArticle = (seoData) => {
  if (!seoData) return 'votre ';
  return seoData.number === "pluriel" ? "vos " : "votre ";
};

// Styles CSS pour Datamaps intégrés
const datamapStyles = `
  .datamap {
    border: none;
    background: transparent;
  }
  .hoverinfo {
    background: rgba(0,0,0,0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.4;
    max-width: 200px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  }
  .datamap-subunit {
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .datamap-subunit:hover {
    stroke-width: 2px !important;
  }
`;

// Injecter les styles dans le head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = datamapStyles;
  document.head.appendChild(styleSheet);
}

const FALLBACK_SECTIONS = [
  {
    title: 'Bienfaits essentiels',
    content: [
      'Riche en micronutriments qui soutiennent l\'énergie quotidienne et l\'immunité.',
      'Apporte des fibres pour une satiété durable et un microbiote équilibré.',
      'Contient des antioxydants qui protègent les cellules du stress oxydatif.'
    ]
  },
  {
    title: 'Associations de saveurs',
    content: [
      'Associez-le avec des agrumes ou des herbes fraîches pour réveiller vos papilles.',
      'Ajoutez une touche croquante avec des graines torréfiées ou des noix.',
      'Mariez-le avec une sauce onctueuse (yaourt, tahini, lait de coco) pour un plat équilibré.'
    ]
  }
];

// Seuils nutritionnels pour le positionnement des valeurs
const NUTRITION_THRESHOLDS = {
  calories: {
    bas: "< 100",
    moyen: "100–250",
    élevé: "> 250"
  },
  protein: {
    bas: "< 5",
    moyen: "5–15",
    élevé: "> 15"
  },
  carbs: {
    bas: "< 10",
    moyen: "10–30",
    élevé: "> 30"
  },
  sugar: {
    bas: "≤ 5",
    moyen: "5–12.5",
    élevé: "> 12.5"
  },
  fat: {
    bas: "≤ 3",
    moyen: "3–20",
    élevé: "> 20"
  },
  saturatedFat: {
    bas: "≤ 1.5",
    moyen: "1.5–5",
    élevé: "> 5"
  },
  fiber: {
    bas: "< 3",
    moyen: "3–6",
    élevé: "> 6"
  },
  salt: {
    bas: "≤ 0.3",
    moyen: "0.3–1.5",
    élevé: "> 1.5"
  },
  glycemicIndex: {
    bas: "< 55",
    moyen: "55–70",
    élevé: "> 70"
  }
};

const formatName = (value) => {
  if (!value) return '';
  return value
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatNutritionValue = (value, unit = 'g') => {
  if (typeof value === 'number') {
    return `${value} ${unit}`;
  }
  return '-';
};

// Fonction pour déterminer le niveau nutritionnel (bas, moyen, élevé)
const getNutritionLevel = (nutrientKey, value) => {
  if (value === undefined || value === null) return '-';
  
  const thresholds = NUTRITION_THRESHOLDS[nutrientKey];
  if (!thresholds) return '-';
  
  const numValue = parseFloat(value);
  
  switch (nutrientKey) {
    case 'calories':
      if (numValue < 100) return 'bas';
      if (numValue <= 250) return 'moyen';
      return 'élevé';
    
    case 'protein':
      if (numValue < 5) return 'bas';
      if (numValue <= 15) return 'moyen';
      return 'élevé';
    
    case 'carbs':
      if (numValue < 10) return 'bas';
      if (numValue <= 30) return 'moyen';
      return 'élevé';
    
    case 'sugar':
      if (numValue <= 5) return 'bas';
      if (numValue <= 12.5) return 'moyen';
      return 'élevé';
    
    case 'fat':
      if (numValue <= 3) return 'bas';
      if (numValue <= 20) return 'moyen';
      return 'élevé';
    
    case 'saturatedFat':
      if (numValue <= 1.5) return 'bas';
      if (numValue <= 5) return 'moyen';
      return 'élevé';
    
    case 'fiber':
      if (numValue < 3) return 'bas';
      if (numValue <= 6) return 'moyen';
      return 'élevé';
    
    case 'salt':
      if (numValue <= 0.3) return 'bas';
      if (numValue <= 1.5) return 'moyen';
      return 'élevé';
    
    case 'glycemicIndex':
      if (numValue < 55) return 'bas';
      if (numValue <= 70) return 'moyen';
      return 'élevé';
    
    default:
      return '-';
  }
};

// Fonction pour obtenir la couleur du niveau nutritionnel
const getNutritionLevelColor = (level) => {
  switch (level) {
    case 'bas': return '#22c55e'; // Vert
    case 'moyen': return '#f59e0b'; // Orange
    case 'élevé': return '#ef4444'; // Rouge
    default: return '#6c757d'; // Gris
  }
};

export default function IngredientBenefitDetailPage() {
  const { Ingredient__slug, Ingredient___id } = useParams();
  const ingredientSlug=Ingredient__slug
  const ingredientId=Ingredient___id
  const [ingredientData, setIngredientData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [combosData, setCombosData] = useState([]);
  const [combosLoading, setCombosLoading] = useState(false);
  const [combosError, setCombosError] = useState(null);
  const mapRef = useRef(null);

  // Fonction pour initialiser la carte Datamaps
  const initializeMap = useCallback((seasonalData) => {
    if (!mapRef.current || !seasonalData) return;

    // Configuration des couleurs par région
    const regionData = {};

    // Mapping complet des régions vers les codes pays Datamaps par continent
    const regionMapping = {
      'NorthAmerica': ['USA', 'CAN', 'MEX', 'GTM', 'BLZ', 'SLV', 'HND', 'NIC', 'CRI', 'PAN', 'CUB', 'JAM', 'HTI', 'DOM', 'TTO', 'BRB', 'GRD', 'VCT', 'LCA', 'DMA', 'ATG', 'KNA', 'BHS'],
      'SouthAmerica': ['BRA', 'ARG', 'CHL', 'PER', 'COL', 'VEN', 'ECU', 'BOL', 'PRY', 'URY', 'GUY', 'SUR', 'GUF'],
      'Europe': ['FRA', 'DEU', 'GBR', 'ITA', 'ESP', 'POL', 'ROU', 'NLD', 'GRC', 'PRT', 'BEL', 'CZE', 'HUN', 'SWE', 'AUT', 'BGR', 'DNK', 'FIN', 'SVK', 'NOR', 'IRL', 'HRV', 'BIH', 'SVN', 'LTU', 'LVA', 'EST', 'MKD', 'ALB', 'MNE', 'SRB', 'LUX', 'MLT', 'ISL', 'CHE', 'MDA', 'UKR', 'BLR', 'RUS'],
      'Asia': ['CHN', 'IND', 'IDN', 'JPN', 'KOR', 'THA', 'VNM', 'MYS', 'SGP', 'PHL', 'PAK', 'BGD', 'AFG', 'UZB', 'KAZ', 'NPL', 'LKA', 'MMR', 'KHM', 'LAO', 'MNG', 'PRK', 'BRN', 'BTN', 'MDV', 'TLS', 'KGZ', 'TJK', 'TKM'],
      'Africa': ['DZA', 'AGO', 'BEN', 'BWA', 'BFA', 'BDI', 'CMR', 'CPV', 'CAF', 'TCD', 'COM', 'COG', 'COD', 'CIV', 'DJI', 'EGY', 'GNQ', 'ERI', 'ETH', 'GAB', 'GMB', 'GHA', 'GIN', 'GNB', 'KEN', 'LSO', 'LBR', 'LBY', 'MDG', 'MWI', 'MLI', 'MRT', 'MUS', 'MAR', 'MOZ', 'NAM', 'NER', 'NGA', 'RWA', 'STP', 'SEN', 'SYC', 'SLE', 'SOM', 'ZAF', 'SSD', 'SDN', 'SWZ', 'TZA', 'TGO', 'TUN', 'UGA', 'ZMB', 'ZWE'],
      'Oceania': ['AUS', 'NZL', 'PNG', 'FJI', 'NCL', 'SLB', 'VUT', 'PYF', 'TON', 'WSM', 'KIR', 'PLW', 'NRU', 'TUV', 'COK', 'NIU', 'MHL', 'FSM'],
      'MiddleEast': ['SAU', 'ARE', 'QAT', 'KWT', 'BHR', 'OMN', 'JOR', 'LBN', 'ISR', 'PSE', 'SYR', 'YEM', 'IRN', 'IRQ', 'TUR']
    };

    // Couleurs simples : vert pour local, orange pour import
    const colors = {
      local: '#22c55e',    // Vert pour production locale
      imported: '#f97316'  // Orange pour importation
    };

    // Appliquer les couleurs selon les données de saisonnalité
    Object.keys(regionMapping).forEach(region => {
      const regionInfo = seasonalData[region];
      if (regionInfo && regionInfo.start) {
        const fillColor = regionInfo.locally_produced ? colors.local : colors.imported;
        
        regionMapping[region].forEach(countryCode => {
          regionData[countryCode] = {
            fillKey: regionInfo.locally_produced ? 'LOCAL' : 'IMPORTED',
            fillColor: fillColor,
            region: region,
            locally_produced: regionInfo.locally_produced,
            period: `${regionInfo.start} - ${regionInfo.finish}`
          };
        });
      }
    });

    // Créer les clés de remplissage simples
    const fills = { 
      'defaultFill': '#e5e7eb',
      'LOCAL': colors.local,
      'IMPORTED': colors.imported
    };

    // Créer la carte Datamaps
    const map = new Datamap({
      element: mapRef.current,
      fills: fills,
      data: regionData,
      geographyConfig: {
        popupTemplate: function(geography, data) {
          if (!data) return '<div class="hoverinfo">Aucune donnée de saisonnalité</div>';
          
          const regionNames = {
            'NorthAmerica': 'Amérique du Nord',
            'SouthAmerica': 'Amérique du Sud', 
            'Europe': 'Europe',
            'Asia': 'Asie',
            'Africa': 'Afrique',
            'Oceania': 'Océanie',
            'MiddleEast': 'Moyen-Orient'
          };
          
          const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 
                         'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
          const [start, finish] = data.period.split(' - ');
          
          return `<div class="hoverinfo">
            <strong>${geography.properties.NAME}</strong><br/>
            <em>${regionNames[data.region] || data.region}</em><br/>
            🗓️ <strong>${months[start-1]} - ${months[finish-1]}</strong><br/>
            ${data.locally_produced ? 
              '🌱 <span style="color: #22c55e;">Production locale</span>' : 
              '🚢 <span style="color: #f97316;">Importation</span>'
            }
          </div>`;
        },
        highlightFillColor: function(data) {
          if (!data) return '#94a3b8';
          return data.locally_produced ? '#16a34a' : '#ea580c';
        },
        highlightBorderColor: '#1e40af',
        highlightBorderWidth: 2,
        borderWidth: 0.5,
        borderColor: '#64748b'
      },
      responsive: true
    });

    // Redimensionner la carte si nécessaire
    window.addEventListener('resize', function() {
      map.resize();
    });
  }, []);

  // Fonction pour récupérer les combos d'ingrédients
  const fetchCombos = useCallback(async (ingredientId) => {
    if (!ingredientId) return;

    try {
      setCombosLoading(true);
      setCombosError(null);

      const response = await api.get('/api/public/combos', {
        params: {
          ingredients: ingredientId,
          exactMatch: false,
          minIngredients: 1,
          limit: 10
        }
      });

      const combos = response?.data?.data || response?.data || [];
      setCombosData(combos);
    } catch (error) {
      console.error('Erreur lors du chargement des combos:', error);
      setCombosError('Impossible de charger les combinaisons d\'ingrédients.');
      setCombosData([]);
    } finally {
      setCombosLoading(false);
    }
  }, []);

  const readableName = useMemo(() => {
    if (ingredientData?.name) return ingredientData.name;
    return formatName(decodeURIComponent(ingredientSlug || ''));
  }, [ingredientSlug, ingredientData?.name]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!ingredientId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await api.get(`/api/public/ingredient/${ingredientId}`);
        if (!isMounted) return;

        const payload = response?.data?.data || response?.data || null;
        console.log('Fetched ingredient data:', payload);
        setIngredientData(payload);
        
        // Récupérer les combos pour cet ingrédient
        if (payload?._id) {
          fetchCombos(payload._id);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Erreur lors du chargement de l\'ingrédient :', err);
        setError('Impossible de récupérer toutes les informations nutritionnelles pour le moment.');
        setIngredientData(null);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [ingredientId, fetchCombos]);

  // Initialiser la carte quand les données sont chargées
  useEffect(() => {
    if (ingredientData?.seo?.seasonalAvailability) {
      // Petit délai pour s'assurer que le DOM est rendu
      setTimeout(() => {
        initializeMap(ingredientData?.seo?.seasonalAvailability);
      }, 100);
    }
  }, [ingredientData, initializeMap]);

  const sections = useMemo(() => {
    const sections = [];
    
    // Section bienfaits
    const benefits = ingredientData?.seo?.benefits;
    let benefitItems;
    let isBenefitsObjects = false;
    
    if (benefits) {
      if (Array.isArray(benefits)) {
        // Vérifier si ce sont des objets avec benefit/description
        if (benefits.length > 0 && typeof benefits[0] === 'object' && benefits[0].benefit) {
          benefitItems = benefits;
          isBenefitsObjects = true;
        } else {
          // Tableau de chaînes de caractères
          benefitItems = benefits;
        }
      } else {
        // Chaîne de caractères à diviser
        benefitItems = benefits.split(/\r?\n+/).filter(Boolean);
      }
    } else {
      benefitItems = FALLBACK_SECTIONS[0].content;
    }
      
    sections.push({
      title: `Quels sont les bienfaits essentiels ${getArticle(ingredientData?.seo, true, readableName)}${readableName.toLowerCase() || 'cet ingrédient'} ?`,
      subtitle: <>Voici <strong>la liste des bienfaits {getArticle(ingredientData?.seo, true, readableName)}{readableName.toLowerCase() || 'cet ingrédient'}</strong> dans votre alimentation. <strong>Les repas avec {getArticle(ingredientData?.seo, true, readableName)}{readableName.toLowerCase() || 'cet ingrédient'}</strong> peut contribuer à votre bien-être quotidien et à une nutrition équilibrée.</>,
      content: benefitItems,
      isBenefits: true,
      isBenefitsObjects: isBenefitsObjects
    });

    // Section valeurs nutritives
    const nutritionData = ingredientData?.seo?.nutritionPer100g;
    if (nutritionData) {
      const nutritionItems = [];
      
      // Macronutriments selon le schéma avec clés pour le calcul des niveaux
      if (nutritionData.calories !== undefined) nutritionItems.push({ name: 'Calories', value: nutritionData.calories, unit: 'kcal', key: 'calories' });
      if (nutritionData.protein !== undefined) nutritionItems.push({ name: 'Protéines', value: nutritionData.protein, unit: 'g', key: 'protein' });
      if (nutritionData.carbs !== undefined) nutritionItems.push({ name: 'Glucides', value: nutritionData.carbs, unit: 'g', key: 'carbs' });
      if (nutritionData.sugar !== undefined) nutritionItems.push({ name: 'Sucres', value: nutritionData.sugar, unit: 'g', key: 'sugar' });
      if (nutritionData.fat !== undefined) nutritionItems.push({ name: 'Lipides', value: nutritionData.fat, unit: 'g', key: 'fat' });
      if (nutritionData.saturatedFat !== undefined) nutritionItems.push({ name: 'Acides gras saturés', value: nutritionData.saturatedFat, unit: 'g', key: 'saturatedFat' });
      if (nutritionData.fiber !== undefined) nutritionItems.push({ name: 'Fibres', value: nutritionData.fiber, unit: 'g', key: 'fiber' });
      if (nutritionData.salt !== undefined) nutritionItems.push({ name: 'Sel', value: nutritionData.salt, unit: 'g', key: 'salt' });
      if (nutritionData.glycemicIndex !== undefined) nutritionItems.push({ name: 'Index glycémique', value: nutritionData.glycemicIndex, unit: '', key: 'glycemicIndex' });

      if (nutritionItems.length > 0) {
        sections.push({
          title: `Quelles sont les valeurs nutritionnelles ${getArticle(ingredientData?.seo, true, readableName)}${readableName.toLowerCase() || 'cet ingrédient'} ? (pour 100g)`,
          content: nutritionItems,
          isNutrition: true
        });
      }
    }

    // Section associations de saveurs
    const flavorPairings = ingredientData?.seo?.flavorPairings;
    const flavorContent = flavorPairings?.length
      ? flavorPairings.map(pairing => {
          const ingredient = pairing.ingredientName;
          const description = pairing.flavourDescription;
          return description ? `${ingredient} : ${description}` : ingredient;
        }).filter(Boolean)
      : FALLBACK_SECTIONS[1].content;
      
    sections.push({
      title: `Comment bien associer ${getArticle(ingredientData?.seo, false, readableName)}${readableName.toLowerCase() || 'cet ingrédient'} et jouer avec les saveurs ?`,
      content: flavorContent
    });

    // Section combos d'ingrédients
    if (combosData?.length > 0) {
      sections.push({
        title: `Quelles sont les meilleures combinaisons d'idées de recettes avec ${getArticle(ingredientData?.seo, false, readableName)}${readableName.toLowerCase() || 'cet ingrédient'} ?`,
        subtitle:<>Découvrez les <strong>idées de recettes avec {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'}</strong> associé à d'autres ingrédients pour créer des plats savoureux et équilibrés.</>,
        content: combosData,
        isCombos: true
      });
    }

    // Section conservation si disponible
    if (ingredientData?.seo?.howToStore) {
      sections.push({
        title: `Nos conseil pour bien conserver ${getArticle(ingredientData?.seo, false, readableName)}${readableName.toLowerCase() || 'cet ingrédient'} :`,
        content: [ingredientData?.seo?.howToStore]
      });
    }

    // Section variétés si disponible
    if (ingredientData?.seo?.varieties?.length) {
      const varietyPrefix = ingredientData?.seo?.number === "pluriel" ? "des " : "de ";
      sections.push({
        title: `Quelles sont les différentes variétés ${varietyPrefix}${readableName.toLowerCase() || 'cet ingrédient'} ?`,
        content: ingredientData?.seo?.varieties,
        isVarieties: true
      });
    }

    // Section choix si disponible
    if (ingredientData?.seo?.howToChoose) {
      sections.push({
        title: `Comment bien choisir ${getPossessiveArticle(ingredientData?.seo)}${readableName.toLowerCase() || 'cet ingrédient'} ?`,
        content: [ingredientData?.seo?.howToChoose]
      });
    }

    return sections;
  }, [ingredientData, readableName, combosData]);

  const metaDescription = ingredientData?.seo?.metaDescription || 
    (ingredientData?.seo?.shortDescription 
      ? `${readableName} : ${ingredientData?.seo?.shortDescription}`.slice(0, 155)
      : `Découvrez les bienfaits, idées saveurs et apports nutritionnels de ${readableName || 'cet ingrédient'} avec YAQAM.`);

  return (
    <>
      <Seo
        title={`YAQAM - Cuisiner ${getArticle(ingredientData?.seo, false, readableName)}${readableName.toLowerCase() || 'cet ingrédient'} : Bienfaits, conseils pratiques et idées de recettes à base de ${readableName.toLowerCase() || 'cet ingrédient'}`}
        description={` ${ingredientData?.seo?.h2 || ''}. Dans cet article, découvrez comment bien cuisiner et associer ${getArticle(ingredientData?.seo, false, readableName)} ${readableName.toLowerCase() || 'cet ingrédient'} dans vos recettes et vos idées repas.`}
        breadcrumb
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "YAQAM",
            "url": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com',
            "logo": "https://www.yaqam.com/img/logo_color-min.png",
            "contactPoint": [{
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": "info@j7lab.com"
            }]
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com'
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Idées de recettes",
                "item": ((typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com') + '/idees-de-repas'
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": (readableName?`Idées de recettes avec ${readableName}`: 'Ingrédient'),
                "item": ((typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com') + (typeof window !== 'undefined' ? window.location.pathname : '')
              }
            ]
          }
        ]}
      />
      
      {/* Bannière avec image de fond */}
      {ingredientData?._id && (
        <div 
          className="relative bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${CDN}/ingredients/banners/${ingredientData._id}.png)`,
            backgroundColor: '#f3f4f6', // Couleur de fallback
            height: '500px' // Hauteur augmentée
          }}
        >
          {/* Overlay gradient pour améliorer la lisibilité */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-black/80 shadow-2xl"></div>
          
          {/* Contenu de la bannière */}
          <div className="relative h-full flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 py-12 w-full text-center">
              <div className="text-white space-y-4">
                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide bg-white/20 backdrop-blur-sm">
                  Bienfaits • Ingrédients • Saveurs
                </span>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight" style={{ color: '#FFFFFF' }}>
                  Cuisiner {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} : Bienfaits, conseils pratiques et idées de recettes à base de {readableName.toLowerCase() || 'cet ingrédient'}
                </h1>
        {ingredientData?.seo?.h2 && (
                    <div>
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold leading-tight max-w-4xl mx-auto" style={{ color: '#FFFFFF' }}>
          {ingredientData?.seo?.h2}. Dans cet article, découvrez comment bien cuisiner et associer {getArticle(ingredientData?.seo, false, readableName)} {readableName.toLowerCase() || 'cet ingrédient'} dans vos recettes et vos repas. Nous vous livrons tous nos conseils pour en retirer tous les bienfaits dans vos plats.
                    </h2>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-gray-100 px-4 py-10">
        <div className="max-w-4xl mx-auto space-y-10">
          


                  
                  {/* Section Dans vos plats avec image */}
                  <section className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#2C3E50' }}>{getArticleCapitalized(ingredientData?.seo, readableName)} {readableName.toLowerCase() || 'cet ingrédient'} dans vos repas</h3>
                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                      {ingredientData?.imageKey && (
                        <div className="lg:w-1/3 flex-shrink-0">
                          <img 
                            src={`${CDN}/${ingredientData.imageKey}`}
                            alt={`Recettes avec ${getArticle(ingredientData?.seo, false, readableName)}${readableName}`}
                            className="w-full h-auto rounded-2xl object-cover"
                            style={{ maxHeight: '300px' }}
                          />
                        </div>
                      )}
                      <div className={`${ingredientData?.imageKey ? 'lg:w-2/3' : 'w-full'}`}>
                        <p className="text-base sm:text-lg mb-4" style={{ color: '#6c757d' }}>
                          {ingredientData?.seo?.shortDescription || 
                           'Explorez les apports nutritionnels, les idées gourmandes et nos astuces pour intégrer cet ingrédient dans vos menus équilibrés.'}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Table des matières */}
                  <section className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                    <h3 className="text-xl font-semibold mb-6" style={{ color: '#2C3E50' }}>Table des matières</h3>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                      <a href="#recettes" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">1.</span>
                        <span>Liste de recettes à base {getArticle(ingredientData?.seo, true, readableName)}{readableName.toLowerCase() || 'cet ingrédient'}</span>
                      </a>
                      <a href="#bienfaits" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">2.</span>
                        <span>Quels sont les bienfaits essentiels {getArticle(ingredientData?.seo, true, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} ?</span>
                      </a>
                      <a href="#nutrition" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">3.</span>
                        <span>Quelles sont les valeurs nutritionnelles {getArticle(ingredientData?.seo, true, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} ? (pour 100g)</span>
                      </a>
                      <a href="#associations" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">4.</span>
                        <span>Comment bien associer {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} et jouer avec les saveurs ?</span>
                      </a>
                      <a href="#combinaisons" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">5.</span>
                        <span>Quelles sont les meilleures combinaisons d'idées de recettes avec {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} ?</span>
                      </a>
                      <a href="#origine" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">6.</span>
                        <span>Comment {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} est arrivée dans nos assiettes ?</span>
                      </a>
                      <a href="#saisonnalite" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">7.</span>
                        <span>Quand consommer {getArticle(ingredientData?.seo, true, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} ? Calendrier de saisonnalité</span>
                      </a>
                      <a href="#varietes" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">8.</span>
                        <span>Quelles sont les différentes variétés {ingredientData?.seo?.number === "pluriel" ? "des " : "de "}{readableName.toLowerCase() || 'cet ingrédient'} ?</span>
                      </a>
                      <a href="#conservation" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">9.</span>
                        <span>Nos conseil pour bien conserver {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} :</span>
                      </a>
                      <a href="#choix" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                        <span className="font-medium">10.</span>
                        <span>Comment bien choisir {getPossessiveArticle(ingredientData?.seo)}{readableName.toLowerCase() || 'cet ingrédient'} ?</span>
                      </a>
                    </div>
                  </section>
                  

                  {isLoading ? (
                  <div className="flex justify-center py-16">
                    <div className="h-10 w-10 rounded-full border-4 border-orange-200 border-t-[#E67E22] animate-spin" />
                  </div>
                  ) : (
                  <div className="space-y-6">
                    {error && (
                    <div className="rounded-2xl border border-orange-200 bg-orange-50/80 px-5 py-4 text-sm" style={{ color: '#92400e' }}>
                      {error}
                    </div>
                    )}
                    {combosError && (
                    <div className="rounded-2xl border border-orange-200 bg-orange-50/80 px-5 py-4 text-sm" style={{ color: '#92400e' }}>
                      {combosError}
                    </div>
                    )}
              {/* Section Recettes ingrédient */}
              {ingredientData?.recipeList?.length > 0 ? (
                <section id="recettes" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                  <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: '#2C3E50' }}>
                      {`Liste de recettes à base de ${readableName.toLowerCase() || 'cet ingrédient'}`}
                  </h3>
                  
                  <p className="text-sm sm:text-base mb-6" style={{ color: '#6c757d' }}>
                    Découvrez une sélection d'<strong>idées de recettes avec {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'}</strong> créées par YAQAM pour ses utilisateurs afin de sublimer {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} dans vos plats du quotidien.
                  </p>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {ingredientData.recipeList.map((recipe, index) => (
                      <article
                        key={recipe._id || index}
                        className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col h-full transition-transform hover:-translate-y-1 hover:shadow-lg"
                      >
                        <Link to={`/idees-de-repas/idees-recette/${recipe.url}/${recipe.recipe_id}`} className="block relative h-48">
                          <img 
                            src={`${CDN}/recipe/${recipe.recipe_id}-0.jpg`}
                            alt={recipe.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/img/placeholder-recipe.jpg'; // Image de fallback si nécessaire
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity"></div>
                        </Link>
                        <div className="p-6 flex flex-col flex-1">
                          <Link 
                            to={`/idees-de-repas/idees-recette/${recipe.url}/${recipe.recipe_id}`} 
                            className="text-lg font-semibold mb-2 hover:underline" 
                            style={{ color: '#2C3E50' }}
                          >
                            {recipe.title}
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : (
                <section id="recettes" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                  <h3 className="text-lg sm:text-xl font-semibold mb-2" style={{ color: '#2C3E50' }}>
                      {`Liste de recettes à base de ${getArticle(ingredientData?.seo, false, readableName)}${readableName.toLowerCase() || 'cet ingrédient'}`}
                  </h3>
                  <p className="text-sm sm:text-base" style={{ color: '#6c757d' }}>
                    Découvrez une <strong>sélection d'idées de recettes avec {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'}</strong> créées par YAQAM pour ses utilisateurs afin de sublimer {getArticle(ingredientData?.seo, false, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} dans vos plats du quotidien.
                  </p>
                </section>
              )}

              {/* Sections dynamiques */}
              <section className="grid gap-6">
                {sections.map((section) => {
                  // Déterminer l'ID approprié basé sur le titre de la section
                  let sectionId = '';
                  if (section.title.includes('bienfaits essentiels')) sectionId = 'bienfaits';
                  else if (section.title.includes('valeurs nutritionnelles')) sectionId = 'nutrition';
                  else if (section.title.includes('bien associer')) sectionId = 'associations';
                  else if (section.title.includes('meilleures combinaisons')) sectionId = 'combinaisons';
                  else if (section.title.includes('différentes variétés')) sectionId = 'varietes';
                  else if (section.title.includes('bien conserver')) sectionId = 'conservation';
                  else if (section.title.includes('bien choisir')) sectionId = 'choix';

                  return (
                  <article
                    key={section.title}
                    id={sectionId}
                    className="rounded-3xl border shadow-sm p-6"
                    style={{ borderColor: '#E3E9ED', background: 'linear-gradient(145deg, #ffffff, #f8fbfc)' }}
                  >
                    <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: '#2C3E50' }}>{section.title}</h3>
                    {section.subtitle && (
                      <p className="text-sm sm:text-base mb-6 leading-relaxed" style={{ color: '#6c757d' }}>
                        {section.subtitle}
                      </p>
                    )}
                    {section.isBenefits && section.isBenefitsObjects ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm sm:text-base">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Bienfait</th>
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {section.content.map((benefit, index) => (
                              <tr key={index} className="border-b border-gray-100 last:border-b-0">
                                <td className="py-3 px-3 font-medium" style={{ color: '#2C3E50' }}>
                                  {benefit.benefit}
                                </td>
                                <td className="py-3 px-3" style={{ color: '#6c757d' }}>
                                  {benefit.description || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (section.title.includes('Comment bien associer') || section.title === 'Associations de saveurs') && ingredientData?.seo?.flavorPairings?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm sm:text-base">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Ingrédient</th>
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Association</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(ingredientData?.seo?.flavorPairings || []).map((pairing, index) => (
                              <tr key={index} className="border-b border-gray-100 last:border-b-0">
                                <td className="py-3 px-3 font-medium" style={{ color: '#2C3E50' }}>
                                  {pairing.ingredientName}
                                </td>
                                <td className="py-3 px-3" style={{ color: '#6c757d' }}>
                                  {pairing.flavourDescription || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : section.isVarieties && section.content?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm sm:text-base">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Variété</th>
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {section.content.map((variety, index) => (
                              <tr key={index} className="border-b border-gray-100 last:border-b-0">
                                <td className="py-3 px-3 font-medium" style={{ color: '#2C3E50' }}>
                                  {variety.name}
                                </td>
                                <td className="py-3 px-3" style={{ color: '#6c757d' }}>
                                  {variety.physicalDescription || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : section.isCombos && section.content?.length ? (
                      <div>
                        {combosLoading && (
                          <div className="flex justify-center py-4 mb-4">
                            <div className="h-6 w-6 rounded-full border-2 border-orange-200 border-t-[#E67E22] animate-spin" />
                          </div>
                        )}
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {section.content.map((combo, index) => (
                            <Link
                              key={combo._id || index}
                              to={generateComboUrl(combo)}
                              className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-lg cursor-pointer block"
                            >
                              {/* Image du combo */}
                              <div className="relative h-32">
                                <img 
                                  src={`${CDN}/combos/${combo._id}.png`}
                                  alt={`Combo ${combo.itemsList?.length ? formatFrenchList(combo.itemsList.map(item => item.name)) : `${index + 1}`}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                              <div className="p-4">
                                <h4 className="text-base font-semibold mb-2" style={{ color: '#2C3E50' }}>
                                  {combo.itemsList?.length ? formatFrenchList(combo.itemsList.map(item => item.name)) : `Combo ${index + 1}`}
                                </h4>
                                {combo.description && combo.description.trim() && (
                                  <p className="text-sm mb-3" style={{ color: '#6c757d' }}>
                                    {combo.description}
                                  </p>
                                )}
                                {combo.itemsList && combo.itemsList.length > 0 && (
                                  <div className="space-y-2">
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                      Ingrédients ({combo.nbIngredients})
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                      {combo.itemsList.map((ingredient, idx) => (
                                        <div
                                          key={ingredient._id || idx}
                                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-full"
                                        >
                                          <img
                                            src={`${CDN}/ingredients/${ingredient._id}.png`}
                                            alt={ingredient.name}
                                            className="w-4 h-4 rounded-full object-cover"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                          <span className="text-xs font-medium" style={{ color: '#374151' }}>
                                            {ingredient.name}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <div className="mt-3 space-y-2">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    {combo.recipeList && combo.recipeList.length > 0 
                                      ? `Recettes disponibles (${combo.recipeList.length})`
                                      : 'Combinaison d\'ingrédients'
                                    }
                                  </span>
                                  <div className="flex justify-center">
                                    <div
                                      className="px-4 py-2 text-xs font-medium rounded-full uppercase text-center"
                                      style={{ 
                                        backgroundColor: '#87A96B',
                                        color: '#ffffff'
                                      }}
                                    >
                                      {combo.recipeList && combo.recipeList.length > 0 
                                        ? <>Idées de recettes <br/>{formatFrenchList(combo.itemsList?.map(item => item.name) || [])}</>
                                        : <>Découvrir la combinaison <br/>{formatFrenchList(combo.itemsList?.map(item => item.name) || [])}</>
                                      }
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                        ))}
                        </div>
                      </div>
                    ) : section.isNutrition && section.content?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm sm:text-base">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Élément nutritif</th>
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Quantité</th>
                              <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2C3E50' }}>Niveau</th>
                            </tr>
                          </thead>
                          <tbody>
                            {section.content.map((nutrient, index) => {
                              const level = getNutritionLevel(nutrient.key, nutrient.value);
                              const levelColor = getNutritionLevelColor(level);
                              
                              return (
                                <tr key={index} className="border-b border-gray-100 last:border-b-0">
                                  <td className="py-3 px-3 font-medium" style={{ color: '#2C3E50' }}>
                                    {nutrient.name}
                                  </td>
                                  <td className="py-3 px-3" style={{ color: '#6c757d' }}>
                                    {nutrient.value !== undefined && nutrient.value !== null ? `${nutrient.value} ${nutrient.unit}` : '-'}
                                  </td>
                                  <td className="py-3 px-3">
                                    <span 
                                      className="px-2 py-1 text-xs font-medium rounded-full capitalize"
                                      style={{ 
                                        backgroundColor: level !== '-' ? `${levelColor}20` : '#f3f4f6',
                                        color: levelColor
                                      }}
                                    >
                                      {level !== '-' ? level : 'N/A'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <ul className="space-y-3 list-disc list-inside text-sm sm:text-base" style={{ color: '#6c757d' }}>
                        {section.content.map((entry, index) => (
                          <li key={`${section.title}-${index}`}>{entry}</li>
                        ))}
                      </ul>
                    )}
                  </article>
                  );
                })}
              </section>

              {/* Section origine */}
              {ingredientData?.seo?.origin && (
                <section id="origine" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                  <h3 className="text-xl font-semibold mb-3" style={{ color: '#2C3E50' }}>Comment {`${getArticle(ingredientData?.seo, false, readableName)}${readableName.toLowerCase()}`} est arrivée dans nos assiettes ?</h3>
                  <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#6c757d' }}>
                    {ingredientData?.seo?.origin}
                  </p>
                </section>
              )}

              {/* Section saisonnalité */}
              {ingredientData?.seo?.seasonalAvailability && (
                <section id="saisonnalite" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                  <h3 className="text-xl font-semibold mb-6" style={{ color: '#2C3E50' }}>
                    Quand consommer {getArticle(ingredientData?.seo, true, readableName)}{readableName.toLowerCase() || 'cet ingrédient'} ? Calendrier de saisonnalité
                  </h3>
                  
                  {/* Vraie carte du monde interactive avec Datamaps */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-2xl">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Carte géographique Datamaps */}
                      <div className="lg:w-2/3">
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ height: '400px' }}>
                          <div 
                            ref={mapRef}
                            style={{ width: '100%', height: '100%' }}
                            className="datamap-container"
                          />
                        </div>
                      </div>
                      
                      {/* Informations détaillées à droite */}
                      <div className="lg:w-1/3 space-y-3">
                        {Object.entries(ingredientData?.seo?.seasonalAvailability || {}).map(([region, data]) => {
                          if (!data.start || !data.finish) return null;
                          
                          const regionNames = {
                            'Africa': 'Afrique',
                            'Asia': 'Asie',
                            'Europe': 'Europe',
                            'MiddleEast': 'Moyen-Orient',
                            'NorthAmerica': 'Amérique du Nord',
                            'Oceania': 'Océanie',
                            'SouthAmerica': 'Amérique du Sud'
                          };
                          
                          const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 
                                        'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
                          
                          return (
                            <div 
                              key={region} 
                              className="bg-white rounded-xl border border-gray-200 p-3 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold" style={{ color: '#2C3E50' }}>
                                  {regionNames[region] || region}
                                </h4>
                                {data.locally_produced && (
                                  <span className="px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: '#EAF4EA', color: '#87A96B' }}>
                                    🌱 Local
                                  </span>
                                )}
                              </div>
                              
                              <div className="text-xs font-medium mb-2" style={{ color: '#E67E22' }}>
                                {months[data.start - 1]} - {months[data.finish - 1]}
                              </div>
                              
                              {/* Barre de progression des mois */}
                              <div className="flex gap-1">
                                {months.map((month, index) => {
                                  const monthNumber = index + 1;
                                  let isInSeason = false;
                                  
                                  if (data.start <= data.finish) {
                                    isInSeason = monthNumber >= data.start && monthNumber <= data.finish;
                                  } else {
                                    isInSeason = monthNumber >= data.start || monthNumber <= data.finish;
                                  }
                                  
                                  return (
                                    <div
                                      key={month}
                                      className={`h-2 flex-1 rounded-sm ${
                                        isInSeason 
                                          ? data.locally_produced 
                                            ? 'bg-green-400' 
                                            : 'bg-orange-400'
                                          : 'bg-gray-200'
                                      }`}
                                      title={`${month} ${isInSeason ? '(en saison)' : ''}`}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Légende */}
                  <div className="flex flex-wrap gap-4 items-center justify-center text-xs" style={{ color: '#6c757d' }}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-2 bg-green-400 rounded-sm"></div>
                      <span>Production locale</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-2 bg-orange-400 rounded-sm"></div>
                      <span>Importation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-2 bg-gray-200 rounded-sm"></div>
                      <span>Hors saison</span>
                    </div>
                  </div>
                </section>
              )}

            </div>
          )}

          <aside className="bg-white rounded-3xl border border-gray-200 shadow-md p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="space-y-2">
              <h3 className="text-xl font-semibold" style={{ color: '#2C3E50' }}>
                Envie d'aller plus loin avec {readableName || 'cet ingrédient'} ?
              </h3>
              <p className="text-sm sm:text-base" style={{ color: '#6c757d' }}>
                YAQAM transforme vos ingrédients préférés en menus personnalisés, équilibrés et savoureux.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Link
                to="/idees-de-repas/idees-recettes"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold"
                style={{ border: '1px solid #87A96B', color: '#87A96B' }}
              >
                Explorer d'autres ingrédients
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold text-white"
                style={{ backgroundColor: '#E67E22' }}
              >
                Créer mon compte YAQAM
              </Link>
            </div>
          </aside>

          {/* Section date de mise à jour */}
          <div className="text-center py-4">
            <p className="text-xs text-gray-500">
              Dernière mise à jour : {ingredientData?.updatedAt ? 
                new Date(ingredientData.updatedAt).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 
                new Date().toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              }
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
